/**
 * Display-only currency conversion.
 *
 * Shopify remains the USD source of truth. This utility reads cached USD FX
 * rates rendered by Liquid and only changes customer-facing text in the DOM.
 */
class DisplayCurrency {
  constructor(source) {
    this.storageKey = 'display_currency';
    this.locale = document.documentElement.lang || 'en';
    this.config = this.readConfig(source);
    this.rates = this.normalizeRates(this.config.fxRatesUsd);
    this.supportedCurrencies = this.normalizeCurrencies(this.config.supportedCurrencies)
      .filter((currency) => currency === 'USD' || Number.isFinite(this.rates[currency]));

    if (!this.supportedCurrencies.length) this.supportedCurrencies = ['USD'];
    this.selectedCurrency = this.readStoredCurrency();

    this.renderCurrencyOptions();
    this.renderCurrencyLabels();
    this.renderPrices();
    this.bindSelection();
    this.observePrices();
  }

  readConfig(source) {
    if (!source) return {};
    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.error('Display currency configuration could not be initialized.', error);
      return {};
    }
  }

  parsePossibleJson(value) {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch (_) {
      return value;
    }
  }

  normalizeCurrencies(value) {
    const parsed = this.parsePossibleJson(value);
    const entries = Array.isArray(parsed) ? parsed : [];
    return [...new Set(entries.map((entry) => {
      const code = typeof entry === 'string' ? entry : entry?.code;
      return String(code || '').trim().toUpperCase();
    }).filter((code) => /^[A-Z]{3}$/.test(code)))];
  }

  normalizeRates(value) {
    const parsed = this.parsePossibleJson(value);
    const ratesSource = this.parsePossibleJson(parsed?.rates) || {};
    const rates = { USD: 1 };

    if (String(parsed?.base || 'USD').toUpperCase() !== 'USD') return rates;
    Object.entries(ratesSource).forEach(([currency, rate]) => {
      const code = String(currency).toUpperCase();
      const numericRate = Number(rate);
      if (code === 'USD') return;
      if (/^[A-Z]{3}$/.test(code) && Number.isFinite(numericRate) && numericRate > 0) {
        rates[code] = numericRate;
      }
    });
    return rates;
  }

  readStoredCurrency() {
    let storedCurrency = '';
    try {
      storedCurrency = window.localStorage.getItem(this.storageKey) || '';
    } catch (_) {
      // USD/default selection remains available when storage is blocked.
    }
    const normalized = storedCurrency.toUpperCase();
    if (this.supportedCurrencies.includes(normalized)) return normalized;
    if (this.supportedCurrencies.includes('USD')) return 'USD';
    return this.supportedCurrencies[0];
  }

  getSelectedCurrency() {
    return this.selectedCurrency;
  }

  setSelectedCurrency(currency) {
    const normalized = String(currency || '').toUpperCase();
    if (!this.supportedCurrencies.includes(normalized)) return false;

    this.selectedCurrency = normalized;
    try {
      window.localStorage.setItem(this.storageKey, normalized);
    } catch (_) {
      // The selection still applies for the current page.
    }

    this.renderCurrencyOptions();
    this.renderCurrencyLabels();
    this.renderPrices();
    window.dispatchEvent(new CustomEvent('display-currency:change', {
      detail: { currency: normalized },
    }));
    return true;
  }

  convertFromUsd(usdCents, currency = this.selectedCurrency) {
    const cents = Number(usdCents);
    const rate = this.rates[currency];
    if (!Number.isFinite(cents) || !Number.isFinite(rate)) return null;
    return (cents / 100) * rate;
  }

  formatCurrency(usdCents, currency = this.selectedCurrency) {
    const amount = this.convertFromUsd(usdCents, currency);
    if (amount === null) return '';
    try {
      // Intl applies separators from the storefront locale and fraction digits
      // from the currency (for example: DKK 2, JPY 0, and KWD 3).
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_) {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(usdCents) / 100);
    }
  }

  currencyName(currency) {
    try {
      return new Intl.DisplayNames([this.locale], { type: 'currency' }).of(currency) || currency;
    } catch (_) {
      return currency;
    }
  }

  currencySymbol(currency) {
    try {
      const parts = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(0);
      return parts.find((part) => part.type === 'currency')?.value || currency;
    } catch (_) {
      return currency;
    }
  }

  renderPrices(root = document) {
    const prices = [];
    if (root instanceof Element && root.matches('[data-price-usd-cents]')) prices.push(root);
    if (root.querySelectorAll) prices.push(...root.querySelectorAll('[data-price-usd-cents]'));

    prices.forEach((price) => {
      const selectedFormatted = this.formatCurrency(price.dataset.priceUsdCents);
      if (price.hasAttribute('data-display-price-with-usd') && this.selectedCurrency !== 'USD') {
        const usdFormatted = this.formatCurrency(price.dataset.priceUsdCents, 'USD');
        if (!usdFormatted || !selectedFormatted) return;
        const prefix = price.dataset.displayPricePrefix;
        const primaryOutput = prefix ? `${prefix} - ${usdFormatted}` : usdFormatted;
        const output = `${primaryOutput} (${selectedFormatted})`;
        if (price.textContent !== output || !price.querySelector('.display-price__converted')) {
          const primary = document.createElement('span');
          primary.className = 'display-price__primary';
          primary.textContent = primaryOutput;
          const converted = document.createElement('span');
          converted.className = 'display-price__converted';
          converted.textContent = ` (${selectedFormatted})`;
          price.replaceChildren(primary, converted);
        }
        price.dataset.displayCurrency = this.selectedCurrency;
        return;
      }
      if (!selectedFormatted) return;
      const prefix = price.dataset.displayPricePrefix;
      const output = prefix ? `${prefix} - ${selectedFormatted}` : selectedFormatted;
      if (price.textContent !== output) price.textContent = output;
      price.dataset.displayCurrency = this.selectedCurrency;
    });
  }

  renderCurrencyLabels() {
    document.querySelectorAll('[data-display-currency-code]').forEach((label) => {
      label.textContent = this.selectedCurrency;
    });
    document.querySelectorAll('[data-display-currency-name]').forEach((label) => {
      label.textContent = this.currencyName(this.selectedCurrency);
    });
    document.querySelectorAll('[data-currency-conversion-disclosure]').forEach((disclosure) => {
      disclosure.hidden = this.selectedCurrency === 'USD';
    });
  }

  renderCurrencyOptions() {
    document.querySelectorAll('[data-display-currency-options]').forEach((list) => {
      const selectedLabel = list.dataset.selectedLabel || '';
      const fragment = document.createDocumentFragment();

      this.supportedCurrencies.forEach((currency) => {
        const item = document.createElement('div');
        item.setAttribute('role', 'listitem');

        const button = document.createElement('button');
        const selected = currency === this.selectedCurrency;
        button.type = 'button';
        button.className = `cy-header__selector-option${selected ? ' is-selected' : ''}`;
        button.dataset.displayCurrencyOption = currency;
        button.setAttribute('aria-pressed', String(selected));

        const symbol = document.createElement('span');
        symbol.className = 'cy-header__selector-symbol';
        symbol.textContent = this.currencySymbol(currency);

        const code = document.createElement('span');
        code.className = 'cy-header__selector-code';
        code.textContent = currency;

        const name = document.createElement('span');
        name.className = 'cy-header__selector-name';
        name.textContent = this.currencyName(currency);

        const state = document.createElement('span');
        state.className = 'cy-header__selected-label';
        state.textContent = selected ? selectedLabel : '';

        button.append(symbol, code, name, state);
        item.append(button);
        fragment.append(item);
      });

      list.replaceChildren(fragment);
    });
  }

  bindSelection() {
    document.addEventListener('click', (event) => {
      const option = event.target.closest('[data-display-currency-option]');
      const dialog = option?.closest('dialog');
      if (!option || !this.setSelectedCurrency(option.dataset.displayCurrencyOption)) return;
      dialog?.close();
    });
  }

  observePrices() {
    if (!document.body || !window.MutationObserver) return;
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) this.renderPrices(node);
        });
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }
}

window.displayCurrency = new DisplayCurrency(document.querySelector('[data-display-currency-config]'));
